import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import notification from "@app/services/notification";
import {
    usePerformBoOperationMutation,
    type PerformBoOperationVariables,
} from "@src/businessModules/WorkShop/shared/bo/usePerformBoOperationMutation";
import { stockActInvsServices, type StockActInvContentUpdateFactQtyData } from "../../../../api/stockActInvs";

/** Параметры хука мутаций карточки полной инвентаризации склада. */
export interface UseStockFullInventoryCardMutationsParams {
    /**
     * Колбэк синхронизации обновлённой строки АИ после сохранения фактического количества.
     * @param contentCode код строки состава АИ
     */
    onContentRefetch?: (contentCode: number) => void | Promise<unknown>;
    /** Колбэк успешной BO-операции. */
    onBoOperationSuccess?: () => void;
}

/** Мутации карточки полной инвентаризации склада. */
export interface UseStockFullInventoryCardMutationsResult {
    /** Обновление фактического количества строки АИ. */
    updateFactQtiesMutation: UseMutationResult<void, Error, StockActInvContentUpdateFactQtyData>;
    /** Выполнение BO-операции над АИ. */
    performBoOperationMutation: UseMutationResult<void, Error, PerformBoOperationVariables>;
}

/** Screen-level мутации карточки полной инвентаризации склада. */
export function useStockFullInventoryCardMutations({
    onContentRefetch,
    onBoOperationSuccess,
}: UseStockFullInventoryCardMutationsParams): UseStockFullInventoryCardMutationsResult {
    const updateFactQtiesMutation = useMutation({
        mutationKey: ["stock", "fullInventory", "updateFactQties"],
        meta: { errorSource: "stock.fullInventory.updateFactQties" },
        mutationFn: (variables: StockActInvContentUpdateFactQtyData) =>
            stockActInvsServices.content.updateFactQties({ data: variables }),
        onSuccess: async (_data, variables) => {
            notification.success("Фактическое количество сохранено");
            await onContentRefetch?.(variables.contentCode);
        },
    });

    const performBoOperationMutation = usePerformBoOperationMutation({
        scope: "stockFullInventory",
        onSuccess: onBoOperationSuccess,
    });

    return {
        updateFactQtiesMutation,
        performBoOperationMutation,
    };
}
